export const RETURN_FROM_PARAM = "returnFrom";
export const RETURN_FROM_IMPERSONATION = "impersonation";

const STORAGE_KEY = "impersonationReturnPath";
const RETURN_PATH_MAX_AGE_MS = 8 * 60 * 60 * 1000;
const EXCLUDED_PATHS = ["/403", "/redirectError", "/ObsoletePage"];

interface StoredReturnPath {
  path: string;
  savedAt: number;
  userId: number;
}

const isSafeReturnPath = (path: string): boolean =>
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("\\") &&
  !EXCLUDED_PATHS.some((excluded) => path.startsWith(excluded));

const parseStoredReturnPath = (raw: string): StoredReturnPath | undefined => {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredReturnPath>;
    if (
      typeof parsed.path !== "string" ||
      typeof parsed.savedAt !== "number" ||
      typeof parsed.userId !== "number"
    ) {
      return undefined;
    }
    return {
      path: parsed.path,
      savedAt: parsed.savedAt,
      userId: parsed.userId,
    };
  } catch {
    return undefined;
  }
};

export const saveImpersonationReturnPath = (userId: number): void => {
  const { pathname, search, hash } = window.location;
  const path = `${pathname}${search}${hash}`;
  if (!isSafeReturnPath(path)) return;
  const stored: StoredReturnPath = { path, savedAt: Date.now(), userId };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.warn("Impersonation return path: unable to save", error);
  }
};

export const consumeImpersonationReturnPath = (
  userId: number,
): string | undefined => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Impersonation return path: unable to read", error);
    return undefined;
  }
  if (!raw) return undefined;
  const stored = parseStoredReturnPath(raw);
  if (!stored) return undefined;
  if (stored.userId !== userId) return undefined;
  if (Date.now() - stored.savedAt > RETURN_PATH_MAX_AGE_MS) return undefined;
  return isSafeReturnPath(stored.path) ? stored.path : undefined;
};

export const applyImpersonationReturnPath = (userId: number): void => {
  const returnPath = consumeImpersonationReturnPath(userId);
  if (!returnPath) return;
  try {
    window.history.replaceState(null, "", returnPath);
  } catch (error) {
    console.warn("Impersonation return path: unable to apply", error);
  }
};
